import React, { useState, useRef } from 'react';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Car, Upload, X } from 'lucide-react';
import API from '../services/api';

const CreerLocation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    titre: '', marque: '', modele: '', annee: '',
    carburant: '', transmission: '', description: '',
    localisation: '', prix_jour: '', prix_semaine: '',
    prix_mois: '', caution: ''
  });

  if (!user) { navigate('/connexion'); return null; }

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(files);
    setPreviews(files.map(f => URL.createObjectURL(f)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErreur('');
    try {
      const res = await API.post('/locations', form);
      const locationId = res.data.location.id;

      if (photos.length > 0) {
        const formData = new FormData();
        photos.forEach(p => formData.append('photos', p));
        await fetch(`${config.API_URL}/api/locations/${locationId}/photos`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
      }

      navigate(`/locations/${locationId}`);
    } catch (err) {
      setErreur(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-yellow-400 transition";
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div style={{ background: '#F5A62320' }} className="w-10 h-10 rounded-xl flex items-center justify-center">
            <Car size={20} style={{ color: '#F5A623' }} />
          </div>
          <div>
            <h1 className="text-white text-2xl font-black">Proposer un véhicule à la location</h1>
            <p className="text-gray-400 text-sm">Remplissez les informations de votre véhicule</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {erreur && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-2xl mb-6 text-sm">
            ⚠️ {erreur}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Infos véhicule */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Informations du véhicule
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Titre</label>
                <input type="text" required value={form.titre}
                  onChange={(e) => setForm({ ...form, titre: e.target.value })}
                  className={inputClass} placeholder="Ex: Toyota Prado 2020 - Location journalière"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Marque</label>
                  <input type="text" required value={form.marque}
                    onChange={(e) => setForm({ ...form, marque: e.target.value })}
                    className={inputClass} placeholder="Toyota"
                  />
                </div>
                <div>
                  <label className={labelClass}>Modèle</label>
                  <input type="text" required value={form.modele}
                    onChange={(e) => setForm({ ...form, modele: e.target.value })}
                    className={inputClass} placeholder="Prado"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Année</label>
                  <input type="number" required value={form.annee}
                    onChange={(e) => setForm({ ...form, annee: e.target.value })}
                    className={inputClass} placeholder="2020"
                  />
                </div>
                <div>
                  <label className={labelClass}>Carburant</label>
                  <select value={form.carburant}
                    onChange={(e) => setForm({ ...form, carburant: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    <option value="essence">Essence</option>
                    <option value="diesel">Diesel</option>
                    <option value="electrique">Électrique</option>
                    <option value="hybride">Hybride</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Transmission</label>
                  <select value={form.transmission}
                    onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Sélectionner</option>
                    <option value="manuelle">Manuelle</option>
                    <option value="automatique">Automatique</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Localisation</label>
                  <input type="text" value={form.localisation}
                    onChange={(e) => setForm({ ...form, localisation: e.target.value })}
                    className={inputClass} placeholder="Brazzaville"
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3} className={inputClass}
                  placeholder="Décrivez votre véhicule..."
                />
              </div>
            </div>
          </div>

          {/* Tarifs */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Tarifs (FCFA)
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Prix/jour *</label>
                <input type="number" required value={form.prix_jour}
                  onChange={(e) => setForm({ ...form, prix_jour: e.target.value })}
                  className={inputClass} placeholder="25000"
                />
              </div>
              <div>
                <label className={labelClass}>Prix/semaine</label>
                <input type="number" value={form.prix_semaine}
                  onChange={(e) => setForm({ ...form, prix_semaine: e.target.value })}
                  className={inputClass} placeholder="150000"
                />
              </div>
              <div>
                <label className={labelClass}>Prix/mois</label>
                <input type="number" value={form.prix_mois}
                  onChange={(e) => setForm({ ...form, prix_mois: e.target.value })}
                  className={inputClass} placeholder="500000"
                />
              </div>
              <div>
                <label className={labelClass}>Caution</label>
                <input type="number" value={form.caution}
                  onChange={(e) => setForm({ ...form, caution: e.target.value })}
                  className={inputClass} placeholder="100000"
                />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Photos
            </h2>
            <div
              onClick={() => fileInputRef.current.click()}
              style={{ border: '2px dashed #e5e7eb' }}
              className="rounded-2xl p-6 text-center cursor-pointer hover:border-yellow-400 transition"
            >
              <Upload size={36} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">Cliquez pour ajouter des photos</p>
            </div>
            <input ref={fileInputRef} type="file" multiple accept="image/*"
              onChange={handlePhotos} className="hidden"
            />
            {previews.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {previews.map((preview, index) => (
                  <div key={index} className="relative">
                    <img src={preview} alt={`apercu`} className="w-full h-32 object-cover rounded-xl" />
                    {index === 0 && (
                      <span style={{ background: '#F5A623' }} className="absolute top-1 left-1 text-black text-xs px-2 py-0.5 rounded-full font-bold">
                        Principale
                      </span>
                    )}
                    <button type="button"
                      onClick={() => {
                        setPhotos(photos.filter((_, i) => i !== index));
                        setPreviews(previews.filter((_, i) => i !== index));
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-4">
            <button type="button" onClick={() => navigate('/mes-locations')}
              className="flex-1 bg-white border border-gray-200 text-gray-600 py-3 rounded-2xl font-bold hover:bg-gray-50 transition"
            >
              Annuler
            </button>
            <button type="submit" disabled={loading}
              style={{ background: '#F5A623' }}
              className="flex-1 text-black py-3 rounded-2xl font-black hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Publication...' : 'Publier la location'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreerLocation;